import { Trans, useTranslation } from 'react-i18next'
import styles from '../BillingSettings.module.css'
import { useUser } from '../../../../contexts/userContext'
import classNames from 'classnames'
import { LicenceCountButton } from '../../../GameEditor/components/AddPerson/components/LicenceCountButton'
import { dateTimeToDashDateString, parseDDMMYYYYcomma } from '../../../../util/string'

type LicenceUsageProps = {
  usedPinSeats: number
  usedPlayerAccountSeats: number
  licenceCount: number
  nextDateWhenLicencesFreed?: string
  autoRenew?: boolean
}

export const LicenceUsage: React.FC<LicenceUsageProps> = ({
  usedPinSeats,
  usedPlayerAccountSeats,
  licenceCount,
  nextDateWhenLicencesFreed,
  autoRenew,
}) => {
  const { t } = useTranslation()
  const { user } = useUser()

  return (
    <div className={styles.subSection}>
      <h3 className={styles.subSectionTitle}>{t('settings_billing.licences_info_title', 'Licence usage')}</h3>
      <div className={styles.detailsList}>
        <p className={styles.licenceInfo}>
          <Trans
            i18nKey='settings_billing.licences_info.info_text'
            values={{
              next_date_when_licences_freed: nextDateWhenLicencesFreed || 'unkown',
              business_name: user?.activeBusiness.name,
            }}
            components={{ br: <br />, strong: <strong /> }}
            defaults={
              'Anonymous players and player accounts add up to fill licences. Anonymous players join games by using a general link of the game, a QR code or they use the game PIN code in the login page. Anonymous player licenses are freed once every three months.<strong> %{business_name} anonymous player licences will renew on %{next_date_when_licences_freed}</strong><br><br>Players with accounts can play multiple games with the same license. You can create player accounts here. All the players invited to a game by email are automatically created an account or the game will be added to their existing account.<br><br>Tags can be added to player accounts.'
            }
          />
        </p>
        <div className={styles.infotableContainer}>
          <div className={styles.infotableRow}>
            <div className={styles.infotableRowLeft}>
              {t('settings_billing.licences_info.infotable.total_amount', 'Total amount of licences')}
            </div>
            <div className={styles.infotableRowRight}>{licenceCount}</div>
          </div>
          <div className={styles.infotableRow}>
            <div className={styles.infotableRowLeft}>
              {t('settings_billing.licences_info.infotable.anonymous_players', 'Anonymous players')}
              <br />
              <div className={styles.extraInfo}>
                {t('settings_billing.licences_info.infotable.anonymous_players_info', 'PIN/QR code')}
              </div>
            </div>
            <div className={styles.infotableRowRight}>-{usedPinSeats}</div>
          </div>
          <div className={styles.infotableRow}>
            <div className={styles.infotableRowLeft}>
              {t('settings_billing.licences_info.infotable.account_players', 'Players with account')}
              <br />
              <div className={styles.extraInfo}>
                {t('settings_billing.licences_info.infotable.account_players_info', 'Email/Player account')}
              </div>
            </div>
            <div className={styles.infotableRowRight}>-{usedPlayerAccountSeats}</div>
          </div>
          <div className={classNames(styles.infotableRow, styles.lastRow)}>
            <LicenceCountButton type='capital' />
          </div>
        </div>
      </div>
      {nextDateWhenLicencesFreed && autoRenew && (
        <p>
          {t('settings_billing.licences_info_next_date_when_licences_freed', {
            defaultValue: 'Next date when licences will be freed: %{next_date_when_licences_freed}',
            next_date_when_licences_freed: dateTimeToDashDateString(parseDDMMYYYYcomma(nextDateWhenLicencesFreed)),
          })}
        </p>
      )}
      {nextDateWhenLicencesFreed && !autoRenew && (
        <p>
          {t('settings_billing.licences_info_licence_valid_date', {
            defaultValue: 'Licences are valid until: %{next_date_when_licences_freed}',
            next_date_when_licences_freed: nextDateWhenLicencesFreed,
          })}
        </p>
      )}
    </div>
  )
}
